import {
  Box,
  Button,
  HStack,
  Image,
  Spacer,
  Tbody,
  Td,
  Text,
  Tr,
} from "@chakra-ui/react";
import ProfitLossTable from "./reportComponents/profitLossTable";

import { Table } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { fetchBalanceSheetData } from "../../api/pdfExport";
import { customCurrencyFormat } from "../../utils";
import ReportCalendarComponent from "./reportComponents/reportDatePicker";
import { format } from "date-fns";

const formatDate = (date) => {
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const BalanceSheetReport = ({
  customer,
  hideButtons = false,
  externalEndDate,
  externalStartDate,
  externalPrintRef,
}) => {
  const [sectionsData, setSectionsData] = useState({});

  const [triggerRefesh, setTriggerRefresh] = useState(true);

  const now = new Date();

  const year = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth();

  const lastDay = new Date(Date.UTC(year, currentMonth, 0));

  const [endDate, setEndDate] = useState(lastDay);

  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: externalPrintRef ? externalPrintRef : printRef,
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 20px 60px;  /* Adjust margin */
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
        }
      }
    `,
    documentTitle: `Balance Sheet Report ${formatDate(
      externalEndDate ? externalEndDate : endDate
    )}`,
  });

  useEffect(() => {
    const controller = new AbortController();

    const fetchAllBalanceSheet = async () => {
      const formattedEndDate = format(externalEndDate || endDate, "yyyy-MM-dd");

      try {
        const response = await fetchBalanceSheetData(
          customer.id,
          formattedEndDate,
          controller.signal
        );

        setSectionsData(response);
      } catch (err) {
        console.error(err);
      }
    };

    fetchAllBalanceSheet();

    return () => {
      controller.abort();
    };
  }, [customer.id, endDate, externalEndDate, triggerRefesh]);

  return (
    <Box width={"auto"} overflowX={"auto"} overflowY={"auto"}>
      {!hideButtons && (
        <HStack mb={"20px"}>
          <Button onClick={handlePrint}>Print / Save as PDF</Button>
          <Spacer />
          <ReportCalendarComponent
            callbackFunctionDate={setEndDate}
            date={endDate}
            triggerSetter={setTriggerRefresh}
          ></ReportCalendarComponent>
        </HStack>
      )}
      <Box ref={externalPrintRef ? externalPrintRef : printRef}>
        <Box
          w="794px"
          bg="white"
          p="40px 60px"
          position="relative"
          css={{
            "@media print": {
              padding: "0 20px !important",
            },
          }}
        >
          <HStack justify="space-between" align="center" mb="20px">
            <Box>
              <Text fontSize="2xl" fontWeight="bold">
                Balance Sheet Report
              </Text>
              <Text fontSize="sm" color="gray.500">
                {customer.name}
              </Text>
              {externalStartDate ? (
                <Text fontSize="sm" color="gray.500">
                  Reporting period from{" "}
                  {formatDate(
                    externalStartDate
                  )}{" "}
                  - {formatDate(externalEndDate ? externalEndDate : endDate)}
                </Text>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  As of{" "}
                  {formatDate(externalEndDate ? externalEndDate : endDate)}
                </Text>
              )}
            </Box>
            <Image
              height="60px"
              objectFit="contain"
              src="../lucro-logo-light.png"
              alt="MoreLucro Logo"
            />
          </HStack>

          <Box overflowX="auto" mb={"2px"}>
            <Table variant="unstyled">
              <Tbody>
                <Tr borderY="2px solid grey">
                  <Td></Td>
                  <Td
                    fontSize="14px"
                    textAlign="right"
                    fontWeight="bold"
                    p={"2px 30px"}
                  >
                    Total
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>

          <ProfitLossTable
            title={"ASSETS"}
            accounts={[
              ...(sectionsData?.current_assets?.accounts || []),
              ...(sectionsData?.fixed_assets?.accounts || []),
              ...(sectionsData?.bank_accounts?.accounts || []),
              ...(sectionsData?.acc_recievable?.accounts || []),
            ]}
          />
          <ProfitLossTable
            title={"LIABILITIES"}
            accounts={[
              ...(sectionsData?.current_liabilities?.accounts || []),
              ...(sectionsData?.long_term_liabilities?.accounts || []),
              ...(sectionsData?.credit_cards?.accounts || []),
              ...(sectionsData?.acc_payable?.accounts || []),
            ]}
          />
          <ProfitLossTable
            title={"EQUITY"}
            accounts={[...(sectionsData?.equity?.accounts || [])]}
          />
          <Table>
            <Tbody>
              <Tr borderTop="1px solid grey" fontSize="14px" fontWeight={800}>
                <Td padding={"2px 10px"}>Total LIABILITIES AND EQUITY</Td>
                <Td textAlign="right" padding={"2px 30px"}>
                  {customCurrencyFormat(
                    sectionsData?.totals?.liabilities_and_equity,
                    false,
                    true,
                    true,
                    true
                  )}
                </Td>
              </Tr>
            </Tbody>
          </Table>
          <Box
            overflowX="auto"
            mt={"2px"}
            borderBottom={"2px solid grey"}
          ></Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BalanceSheetReport;
